import type { BookingRulesEntry, BookingRulesSettings } from "@planerius/contracts/cabinet"
import { type ReactNode, useCallback, useEffect, useMemo, useState } from "react"

import { useCabinetAccess, useHasPermission } from "../../access/use-access"
import { type ApiProblem, isApiProblem } from "../../api/problem"
import { fetchBookingRules, saveBookingRules } from "../../api/settings.api"
import { ProblemScreen, Skeleton } from "../../shell/states"
import { Badge, Button } from "../../ui"
import { StandHeader } from "../directories/stand"
import {
  POLICY_OPTIONS,
  RULE_ROWS,
  TEXT_BLACKLIST_SCOPE,
  branchDifferences,
  changedFields,
  deadlineWarning,
} from "./booking-rules-model"
import styles from "./booking-rules.module.css"

/**
 * 4-Б4 «Правила записи» — /settings/booking-rules.
 *
 * ЭКРАН ПРО ПОСЛЕДСТВИЯ, А НЕ ПРО ПОЛЯ. Каждая строка подписана тем, что случится в зале:
 * «ноль» в сроке записи — это клиент, записавшийся на время через пять минут. Отдельно
 * стоит предупреждение о сочетании «отменить нельзя раньше, чем успеешь записаться»:
 * это не запрет, салон вправе так работать, но узнать об этом он должен до сохранения,
 * а не из жалобы клиента.
 *
 * Правила филиала перекрывают салонные, поэтому сверху — переключатель области, а рядом
 * с ним отметка, чем филиал отличается: иначе владелец правит салон и не понимает,
 * почему на Гагарина по-прежнему по-своему.
 */
export function BookingRulesScreen(): ReactNode {
  const { context } = useCabinetAccess()
  const canManage = useHasPermission("booking_rules.manage")

  const [settings, setSettings] = useState<BookingRulesSettings | null>(null)
  const [problem, setProblem] = useState<ApiProblem | null>(null)
  const [loadFailed, setLoadFailed] = useState<ApiProblem | null>(null)
  const [scope, setScope] = useState<string | null>(null)
  const [draft, setDraft] = useState<BookingRulesEntry | null>(null)
  const [busy, setBusy] = useState(false)
  const [saved, setSaved] = useState(false)

  const load = useCallback(async (signal?: AbortSignal): Promise<void> => {
    try {
      const next = await fetchBookingRules(signal)
      setSettings(next)
      setLoadFailed(null)
    } catch (error) {
      if (signal?.aborted === true) return
      if (!isApiProblem(error)) throw error
      setLoadFailed(error)
    }
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    void load(controller.signal)
    return () => controller.abort()
  }, [load])

  /** Что сейчас правим: салон или филиал, у которого есть своя строка либо её ещё нет. */
  const current = useMemo<BookingRulesEntry | null>(() => {
    if (settings === null) return null
    if (scope === null) return settings.salon
    const own = settings.branches.find((branch) => branch.branchId === scope)
    if (own !== undefined) return own
    // У филиала своей строки нет: действуют салонные правила, и первая правка их и запишет.
    const branch = context.branches.find((item) => item.id === scope)
    return { ...settings.salon, branchId: scope, branchName: branch?.name ?? null, updatedAt: null, isPlatformDefault: settings.salon.isPlatformDefault }
  }, [settings, scope, context.branches])

  useEffect(() => {
    setDraft(current)
    setSaved(false)
  }, [current])

  const differences = useMemo(() => (settings === null ? [] : branchDifferences(settings)), [settings])

  if (loadFailed !== null && settings === null) {
    return <ProblemScreen problem={loadFailed} onRetry={() => void load()} />
  }

  const warning = draft === null ? null : deadlineWarning(draft)
  const patch = current === null || draft === null ? {} : changedFields(current, draft)
  const dirty = Object.keys(patch).length > 0

  const save = async (): Promise<void> => {
    if (draft === null || current === null || !dirty) return
    setBusy(true)
    setProblem(null)
    try {
      await saveBookingRules({
        branchId: draft.branchId,
        expectedUpdatedAt: current.updatedAt,
        ...patch,
      })
      setSaved(true)
      await load()
    } catch (error) {
      if (!isApiProblem(error)) throw error
      setProblem(error)
      // Версию могли сменить в соседней вкладке — перечитываем, чтобы показать её значения.
      if (error.code === "version_conflict") await load()
    } finally {
      setBusy(false)
    }
  }

  const set = (field: keyof BookingRulesEntry, value: number | string | boolean): void => {
    setDraft((previous) => (previous === null ? previous : { ...previous, [field]: value }))
    setSaved(false)
  }

  return (
    <div className={styles.screen}>
      <StandHeader title="Правила записи">
        {draft?.isPlatformDefault === true && draft.updatedAt === null ? (
          <Badge tone="info">наши рекомендации</Badge>
        ) : null}
      </StandHeader>

      {settings === null ? (
        <div className={styles.skeletons}>
          {[0, 1, 2, 3, 4].map((row) => (
            <Skeleton key={row} width="100%" height={44} radius={10} />
          ))}
        </div>
      ) : draft === null ? null : (
        <div className={styles.column}>
          {context.branches.length <= 1 && differences.length === 0 ? null : (
            <div className={styles.scopes}>
              <Button size="sm" variant={scope === null ? "primary" : "secondary"} onClick={() => setScope(null)}>
                Правила салона
              </Button>
              {context.branches.map((branch) => {
                const difference = differences.find((item) => item.branchId === branch.id)
                return (
                  <Button
                    key={branch.id}
                    size="sm"
                    variant={scope === branch.id ? "primary" : "secondary"}
                    onClick={() => setScope(branch.id)}
                  >
                    {branch.name}
                    {difference === undefined ? "" : ` · отличается ${String(difference.count)}`}
                  </Button>
                )
              })}
            </div>
          )}

          {scope !== null && draft.updatedAt === null ? (
            <p className={styles.hint}>
              У филиала своих правил нет — действуют правила салона. Первое сохранение запишет их филиалу целиком,
              и дальше салонные на него влиять не будут.
            </p>
          ) : null}

          <div className={styles.rules}>
            {RULE_ROWS.map((rule) => (
              <div key={rule.field} className={styles.rule}>
                <div className={styles.ruleText}>
                  <div className={styles.ruleName}>{rule.name}</div>
                  <div className={styles.ruleHint}>{rule.hint}</div>
                </div>
                <select
                  className={styles.ruleField}
                  aria-label={rule.name}
                  disabled={!canManage}
                  value={String(draft[rule.field])}
                  onChange={(event) => set(rule.field, Number(event.target.value))}
                >
                  {rule.options.map((option) => (
                    <option key={option.value} value={String(option.value)}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>

          {warning === null ? null : (
            <div className={styles.warning} role="status">
              {warning}
            </div>
          )}

          <div className={styles.policy}>
            <div className={styles.policyHead}>
              При неявках — после{" "}
              <select
                className={styles.threshold}
                aria-label="Сколько неявок подряд"
                disabled={!canManage}
                value={String(draft.noShowThreshold)}
                onChange={(event) => set("noShowThreshold", Number(event.target.value))}
              >
                {[1, 2, 3, 4, 5].map((value) => (
                  <option key={value} value={String(value)}>
                    {value}
                  </option>
                ))}
              </select>{" "}
              подряд
            </div>
            <div className={styles.chips}>
              {POLICY_OPTIONS.map((option) => (
                <Button
                  key={option.value}
                  size="sm"
                  variant={draft.noShowPolicy === option.value ? "primary" : "secondary"}
                  deniedReason={
                    option.deniedReason ?? (canManage ? undefined : "Правила записи меняет владелец или управляющий")
                  }
                  onClick={() => set("noShowPolicy", option.value)}
                >
                  {option.label}
                </Button>
              ))}
            </div>
            <p className={styles.hint}>{TEXT_BLACKLIST_SCOPE}</p>
          </div>

          {problem === null ? null : (
            <div className={styles.error} role="alert">
              <b>{problem.title}</b> {problem.detail ?? ""}
            </div>
          )}
          {saved ? <div className={styles.saved} role="status">Правила сохранены.</div> : null}

          <div className={styles.footer}>
            <Button
              variant="primary"
              loading={busy}
              loadingLabel="Сохраняем…"
              deniedReason={
                !canManage
                  ? "Правила записи меняет владелец или управляющий — вы видите их только на чтение"
                  : dirty
                    ? undefined
                    : "Менять нечего"
              }
              onClick={() => void save()}
            >
              Сохранить
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
